import React, { useState, useEffect, useCallback } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

export function useAuthState() {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [initialCheckComplete, setInitialCheckComplete] = useState(false);

  const loadProfile = useCallback(async (currentUser: User | null) => {
    if (currentUser) {
      try {
        console.log('🔐 AUTH: Loading profile for user:', currentUser.email);
        
        // Try to load from profiles table first (Supabase users)
        const { data: supabaseProfile } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', currentUser.id)
          .single();

        if (supabaseProfile) {
          console.log('🔐 AUTH: Supabase profile found and loaded');
          setProfile(supabaseProfile);
          return;
        }

        // If not found, try internal_user_profiles
        const { data: internalProfile } = await supabase
          .from('internal_user_profiles')
          .select('*')
          .eq('user_id', currentUser.id)
          .single();

        if (internalProfile) {
          console.log('🔐 AUTH: Internal profile found and loaded');
          setProfile(internalProfile);
        } else {
          console.log('🔐 AUTH: No profile found for user, creating basic profile');
          // For new Supabase users, create a basic profile entry
          const { error: insertError } = await supabase
            .from('profiles')
            .insert({
              id: currentUser.id,
              email: currentUser.email || '',
              username: currentUser.user_metadata?.username || currentUser.email?.split('@')[0] || 'User',
              role: 'player'
            });
            
          if (!insertError) {
            // Reload the newly created profile
            const { data: newProfile } = await supabase
              .from('profiles')
              .select('*')
              .eq('id', currentUser.id)
              .single();
            setProfile(newProfile);
            console.log('🔐 AUTH: Basic profile created and loaded');
          } else {
            console.error('🔐 AUTH: Error creating basic profile:', insertError);
            setProfile(null);
          }
        }
      } catch (error) {
        console.error('Error loading profile:', error);
        setProfile(null);
      }
    } else {
      setProfile(null);
    }
  }, []);

  const refreshProfile = useCallback(async () => {
    if (user) {
      console.log('🔐 AUTH: Manual profile refresh requested');
      await loadProfile(user);
    }
  }, [user, loadProfile]);

  useEffect(() => {
    let mounted = true;
    
    // Set up auth state listener FIRST to catch any auth events
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!mounted) return;
        
        console.log('🔐 AUTH: Auth state changed:', event, session?.user?.email);
        
        // Handle auth events synchronously to prevent deadlocks
        setSession(session);
        setUser(session?.user ?? null);
        
        if (event === 'SIGNED_OUT') {
          setProfile(null);
        }
        
        // Mark initialization as complete after first auth event
        if (!initialCheckComplete) {
          setInitialCheckComplete(true);
        }
        setLoading(false);
      }
    );

    // THEN check for existing session to handle immediate page loads
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!mounted) return;
      
      console.log('🔐 AUTH: Initial session check:', session?.user?.email);
      
      // This handles the case where auth listener hasn't fired yet
      setSession(session);
      setUser(session?.user ?? null);
      
      if (!initialCheckComplete) {
        setInitialCheckComplete(true);
      }
      setLoading(false);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // Load profile data when user changes
  useEffect(() => {
    // Only load profile after initial check is complete to avoid race conditions
    if (initialCheckComplete) {
      loadProfile(user);
    }
  }, [user, initialCheckComplete, loadProfile]);

  return {
    user,
    session,
    profile,
    loading,
    initialCheckComplete,
    refreshProfile
  };
}